import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Filter,
  Calendar,
  User,
  Activity,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import {
  useAuditLogs,
  AUDIT_ACTIONS,
  getActionDescription,
  getActionCategory,
  type AuditAction,
} from '../hooks/useAuditLogs';
import { useProjects } from '../hooks/useProjects';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Format a date for display
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get badge variant for action category
 */
function getActionVariant(action: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  const category = getActionCategory(action);
  switch (category) {
    case 'create':
      return 'default';
    case 'read':
      return 'secondary';
    case 'update':
      return 'outline';
    case 'delete':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Page for viewing audit logs with filters
 */
export function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedProject } = useProjects();

  // Get filters from URL params
  const projectId = searchParams.get('projectId') || selectedProject?.id || '';
  const userFilter = searchParams.get('user') || '';
  const actionFilter = (searchParams.get('action') || '') as AuditAction | '';
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);

  // Local state for filter inputs
  const [localUserFilter, setLocalUserFilter] = useState(userFilter);
  const [localActionFilter, setLocalActionFilter] = useState<AuditAction | ''>(actionFilter);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [showFilters, setShowFilters] = useState(
    !!(userFilter || actionFilter || startDate || endDate)
  );

  // Fetch audit logs
  const {
    logs,
    total,
    currentPage,
    totalPages,
    isLoading,
    isFetching,
    refetch,
  } = useAuditLogs({
    teamId: selectedProject?.team_id,
    projectId: projectId || undefined,
    userId: userFilter || undefined,
    action: actionFilter || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    page,
    pageSize: 25,
  });

  // Apply filters
  const applyFilters = () => {
    const params = new URLSearchParams();
    if (projectId) params.set('projectId', projectId);
    if (localUserFilter) params.set('user', localUserFilter);
    if (localActionFilter) params.set('action', localActionFilter);
    if (localStartDate) params.set('startDate', localStartDate);
    if (localEndDate) params.set('endDate', localEndDate);
    params.set('page', '1'); // Reset to first page when applying filters
    setSearchParams(params);
  };

  // Clear filters
  const clearFilters = () => {
    setLocalUserFilter('');
    setLocalActionFilter('');
    setLocalStartDate('');
    setLocalEndDate('');
    const params = new URLSearchParams();
    if (projectId) params.set('projectId', projectId);
    setSearchParams(params);
  };

  // Navigate to page
  const goToPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  return (
    <motion.div 
      className="max-w-6xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div>
          <h1 className="text-2xl font-bold text-foreground">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track all actions and changes in your project
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <motion.div whileTap={{ scale: 0.95 }}>
            <Button
              variant={showFilters ? 'secondary' : 'outline'}
              onClick={() => setShowFilters(!showFilters)}
              className="transition-all"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {(userFilter || actionFilter || startDate || endDate) && (
                <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary/20 text-xs text-primary">
                  {[userFilter, actionFilter, startDate, endDate].filter(Boolean).length}
                </span>
              )}
            </Button>
          </motion.div>
          <motion.div whileTap={{ scale: 0.95 }}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Refresh"
              className="hover:bg-muted/50"
            >
              <RefreshCw className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
          </motion.div>
        </div>
      </motion.div>

      {/* Filters panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
            animate={{ opacity: 1, height: 'auto', marginBottom: 24 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <Card className="glass border-border/50 overflow-hidden">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* User filter */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <User className="h-3.5 w-3.5" />
                      User
                    </Label>
                    <div className="relative group">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-colors group-focus-within:text-primary" />
                      <Input
                        type="text"
                        value={localUserFilter}
                        onChange={(e) => setLocalUserFilter(e.target.value)}
                        placeholder="Filter by email..."
                        className="pl-9 bg-muted/30 border-transparent focus:border-primary/50 focus:bg-muted/50 transition-all"
                      />
                      <AnimatePresence>
                        {localUserFilter && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className="absolute right-1 top-1/2 -translate-y-1/2"
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setLocalUserFilter('')}
                              className="h-6 w-6"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Action filter */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Activity className="h-3.5 w-3.5" />
                      Action
                    </Label>
                    <select
                      value={localActionFilter}
                      onChange={(e) => setLocalActionFilter(e.target.value as AuditAction | '')}
                      className="flex h-10 w-full rounded-md bg-muted/30 border-transparent px-3 py-2 text-sm focus:border-primary/50 focus:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer"
                    >
                      {AUDIT_ACTIONS.map((action) => (
                        <option key={action.value} value={action.value}>
                          {action.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Start date */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Calendar className="h-3.5 w-3.5" />
                      Start Date
                    </Label>
                    <Input
                      type="date"
                      value={localStartDate}
                      onChange={(e) => setLocalStartDate(e.target.value)}
                      className="bg-muted/30 border-transparent focus:border-primary/50 focus:bg-muted/50 transition-all"
                    />
                  </div>

                  {/* End date */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Calendar className="h-3.5 w-3.5" />
                      End Date
                    </Label>
                    <Input
                      type="date"
                      value={localEndDate}
                      onChange={(e) => setLocalEndDate(e.target.value)}
                      className="bg-muted/30 border-transparent focus:border-primary/50 focus:bg-muted/50 transition-all"
                    />
                  </div>
                </div>

                {/* Filter actions */}
                <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-border/50">
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="ghost"
                      onClick={clearFilters}
                      className="hover:bg-muted/50"
                    >
                      Clear
                    </Button>
                  </motion.div>
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Button 
                      onClick={applyFilters}
                      className="shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-shadow"
                    >
                      Apply Filters
                    </Button>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Audit log table */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="glass border-border/50 overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-muted/30 border-b border-border/50 px-4 py-3">
                  <div className="flex gap-8">
                    {['Timestamp', 'User', 'Action', 'Resource', 'Environment'].map((_, i) => (
                      <div key={i} className="h-3 w-20 bg-muted/50 rounded animate-shimmer" />
                    ))}
                  </div>
                </div>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="flex items-center gap-8 px-4 py-4 border-b border-border/50 last:border-b-0">
                    <div className="h-4 w-32 bg-muted/50 rounded animate-shimmer" />
                    <div className="h-4 w-40 bg-muted/50 rounded animate-shimmer" />
                    <div className="h-6 w-20 bg-muted/50 rounded-full animate-shimmer" />
                    <div className="space-y-1.5 flex-1">
                      <div className="h-4 w-24 bg-muted/50 rounded animate-shimmer" />
                      <div className="h-3 w-16 bg-muted/30 rounded animate-shimmer" />
                    </div>
                    <div className="h-4 w-16 bg-muted/30 rounded animate-shimmer" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ) : logs.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="glass border-border/50">
              <CardContent className="px-4 py-16 text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                  className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-muted/50 mb-4"
                >
                  <Activity className="h-8 w-8 text-muted-foreground" />
                </motion.div>
                <motion.h3 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-lg font-medium text-foreground"
                >
                  No audit logs found
                </motion.h3>
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto"
                >
                  {userFilter || actionFilter || startDate || endDate
                    ? 'Try adjusting your filters to see more results.'
                    : 'Actions in this project will appear here.'}
                </motion.p>
                {(userFilter || actionFilter || startDate || endDate) && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-6"
                  >
                    <Button variant="outline" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="glass border-border/50 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/30 border-b border-border/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Resource
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Environment
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {logs.map((log, index) => (
                      <motion.tr 
                        key={log.id} 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03, duration: 0.2 }}
                        whileHover={{ backgroundColor: 'hsl(var(--accent) / 0.5)' }}
                        className="transition-colors cursor-default"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground font-mono">
                          {formatDate(log.createdAt)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-xs font-medium text-primary">
                                {log.userId ? 'U' : 'S'}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-foreground truncate max-w-[200px]">
                              {log.userId ? 'User' : 'Service Token'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge variant={getActionVariant(log.action)} className="font-medium">
                            {getActionDescription(log.action)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-foreground">
                            {log.resourceName || log.resourceId}
                          </div>
                          <div className="text-xs text-muted-foreground">{log.resourceType}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {log.environmentName ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted/50 text-xs font-medium text-muted-foreground">
                              {log.environmentName}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground/50">—</span>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center justify-between px-4 py-3 bg-muted/20 border-t border-border/50"
                >
                  <div className="text-sm text-muted-foreground">
                    Showing <span className="font-medium text-foreground">{(currentPage - 1) * 25 + 1}</span> to{' '}
                    <span className="font-medium text-foreground">{Math.min(currentPage * 25, total)}</span> of{' '}
                    <span className="font-medium text-foreground">{total}</span> entries
                  </div>
                  <div className="flex items-center gap-1">
                    <motion.div whileTap={{ scale: 0.9 }}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="h-8 w-8 hover:bg-muted/50"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                    </motion.div>
                    <div className="flex items-center gap-1 px-2">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <motion.div key={pageNum} whileTap={{ scale: 0.9 }}>
                            <Button
                              variant={currentPage === pageNum ? 'secondary' : 'ghost'}
                              size="sm"
                              onClick={() => goToPage(pageNum)}
                              className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-primary/20 text-primary hover:bg-primary/30' : 'hover:bg-muted/50'}`}
                            >
                              {pageNum}
                            </Button>
                          </motion.div>
                        );
                      })}
                    </div>
                    <motion.div whileTap={{ scale: 0.9 }}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="h-8 w-8 hover:bg-muted/50"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </motion.div>
                  </div>
                </motion.div>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
